/**
 * View Icon Picker
 *
 * A 10x5 grid popover for selecting an icon for custom views.
 */

import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { AVAILABLE_ICON_NAMES, getIconByName } from '../utils/viewIcons';

interface ViewIconPickerProps {
  currentIconName?: string;
  onSelect: (iconName: string) => void;
  onClose: () => void;
}

export function ViewIconPicker({
  currentIconName,
  onSelect,
  onClose,
}: ViewIconPickerProps) {
  const pickerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Close on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleIconClick = (iconName: string) => {
    onSelect(iconName);
    onClose();
  };

  return (
    <div
      ref={pickerRef}
      className="absolute left-0 top-full mt-2 z-50 bg-surface-raised-light dark:bg-surface-raised-dark rounded-lg shadow-lg border border-border-subtle-light dark:border-border-subtle-dark p-4 w-[340px]"
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">
          Choose Icon
        </span>
        <button
          type="button"
          onClick={onClose}
          className="p-1 hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded-md transition-colors"
        >
          <X className="w-4 h-4 text-text-tertiary-light" />
        </button>
      </div>

      <div className="grid grid-cols-10 gap-1.5">
        {AVAILABLE_ICON_NAMES.map((iconName) => {
          const IconComponent = getIconByName(iconName);
          const isSelected = currentIconName === iconName;

          return (
            <button
              key={iconName}
              type="button"
              onClick={() => handleIconClick(iconName)}
              title={iconName}
              className={`
                p-1 rounded-md transition-colors flex items-center justify-center
                ${isSelected
                  ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 ring-2 ring-orange-500'
                  : 'hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark text-text-secondary-light dark:text-text-secondary-dark'
                }
              `}
            >
              <IconComponent className="w-5 h-5" />
            </button>
          );
        })}
      </div>
    </div>
  );
}
